import { useEffect, useRef } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Building2, Lock } from 'lucide-react';
import { Stepper } from './Stepper';
import { StepCompanyLookup } from './StepCompanyLookup';
import { StepContact } from './StepContact';
import { StepEnergyType } from './StepEnergyType';
import { StepUsages } from './StepUsages';
import { StepBills } from './StepBills';
import { StepReview } from './StepReview';
import { StepThankYou } from './StepThankYou';
import { useComparateurStore } from './useComparateurStore';
import { getAudienceById, type AudienceId } from '@shared/constants/audiences';
import { easeStripe } from '../../../lib/motion';
import { captureEvent } from '../../../lib/analytics';
import {
  trackFunnelStart,
  trackStepView,
  trackFunnelAbandon,
  readAttribution,
  type FunnelContext,
} from '../../../lib/tracking';
import { Badge } from '../../ui/badge';
import type { ComparateurDossierPublic } from '@shared/schemas/comparateur';

export { useComparateurStore } from './useComparateurStore';

interface ComparateurFunnelProps {
  initialDossier?: ComparateurDossierPublic | null;
  /**
   * Verrouille le profil sur cette audience.
   * Affiche un badge contextuel et marque le dossier comme sectoriel.
   */
  lockedAudience?: AudienceId;
}

function readUtmFromLocation(): Record<string, string> | undefined {
  if (typeof window === 'undefined') return undefined;
  const attr = readAttribution();
  const utm: Record<string, string> = {};
  if (attr.utmSource) utm.source = attr.utmSource;
  if (attr.utmMedium) utm.medium = attr.utmMedium;
  if (attr.utmCampaign) utm.campaign = attr.utmCampaign;
  if (attr.utmContent) utm.content = attr.utmContent;
  if (attr.utmTerm) utm.term = attr.utmTerm;
  return Object.keys(utm).length > 0 ? utm : undefined;
}

const COMPARATEUR_CTX: FunnelContext = {
  funnelId: 'comparateur',
  intent: 'comparer',
  source: 'comparateur',
};

const STEP_INDEX: Record<string, number> = {
  lookup: 0,
  contact: 1,
  energy: 2,
  usages: 3,
  bills: 4,
  review: 5,
  thank_you: 6,
};

export function ComparateurFunnel({ initialDossier, lockedAudience }: ComparateurFunnelProps) {
  const lockedAudienceMeta = lockedAudience ? getAudienceById(lockedAudience) : null;
  const store = useComparateurStore();

  useEffect(() => {
    if (initialDossier) {
      store.initFromDossier(initialDossier);
      return;
    }
    if (!store.dossier) {
      // funnel_start sur le premier montage (pas une reprise)
      trackFunnelStart({ ...COMPARATEUR_CTX, audience: lockedAudience });
      void store.startNewDossier(readUtmFromLocation()).catch(() => {
        /* error displayed in store.error by the first step */
      });
    } else {
      // Reprise d'un dossier existant (initialDossier passé depuis la page de reprise)
      captureEvent('funnel_resume', {
        funnel_id: 'comparateur',
        step: store.step,
        step_index: STEP_INDEX[store.step] ?? 0,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Tracking standardisé : step_view à chaque changement d'étape.
  useEffect(() => {
    trackStepView(
      { ...COMPARATEUR_CTX, audience: lockedAudience },
      store.step,
      STEP_INDEX[store.step] ?? 0,
    );
  }, [store.step, lockedAudience]);

  // Abandon : sendBeacon via captureEvent au déchargement de la page
  useEffect(() => {
    const onUnload = () => {
      if (store.step !== 'thank_you') {
        trackFunnelAbandon(
          { ...COMPARATEUR_CTX, audience: lockedAudience },
          store.step,
          STEP_INDEX[store.step] ?? 0,
        );
      }
    };
    window.addEventListener('pagehide', onUnload);
    return () => window.removeEventListener('pagehide', onUnload);
  }, [store.step, lockedAudience]);

  // Scroll to top du funnel à chaque changement d'étape (mobile : step suivant hors écran sinon)
  // + focus du contenu de l'étape après la transition (a11y clavier/SR).
  const skippedFirstFocus = useRef(false);
  useEffect(() => {
    const el = document.getElementById('comparateur-funnel-top');
    el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    if (!skippedFirstFocus.current) {
      skippedFirstFocus.current = true;
      return;
    }
    const t = window.setTimeout(() => {
      document.getElementById('comparateur-step-content')?.focus({ preventScroll: true });
    }, 300);
    return () => window.clearTimeout(t);
  }, [store.step]);

  return (
    <div id="comparateur-funnel-top" className="relative overflow-hidden rounded-2xl border border-stone-200 bg-white shadow-[0_8px_32px_rgba(0,0,0,.07)]">
      {lockedAudienceMeta && (
        <div className="border-b border-stone-100 bg-stone-50/60 px-5 py-2.5 md:px-8">
          <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-stone-600">
            <Lock className="h-2.5 w-2.5" strokeWidth={2.5} />
            Parcours {lockedAudienceMeta.shortLabel}
          </span>
        </div>
      )}
      <Stepper currentStep={store.step} />

      <AnimatePresence>
        {store.dossier?.company && store.step !== 'lookup' && store.step !== 'thank_you' && (
          <motion.div
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.2, ease: easeStripe }}
            className="flex items-center gap-2.5 border-b border-brand-100 bg-brand-50/60 px-5 py-2.5 md:px-8"
          >
            <Building2 className="h-3.5 w-3.5 shrink-0 text-brand-600" />
            <span className="truncate text-[12px] font-semibold text-brand-800">
              {store.dossier.company.name}
            </span>
            {store.dossier.company.nafCode && (
              <Badge variant="default" size="sm" className="ml-auto shrink-0">
                NAF {store.dossier.company.nafCode}
              </Badge>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      <div className="p-5 md:p-8">
        <AnimatePresence mode="wait">
          <motion.div
            key={store.step}
            id="comparateur-step-content"
            tabIndex={-1}
            className="outline-none"
            initial={{ opacity: 0, x: 24 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -24 }}
            transition={{ duration: 0.28, ease: easeStripe }}
          >
            {store.step === 'lookup' && <StepCompanyLookup />}
            {store.step === 'contact' && <StepContact />}
            {store.step === 'energy' && <StepEnergyType />}
            {store.step === 'usages' && <StepUsages />}
            {store.step === 'bills' && <StepBills />}
            {store.step === 'review' && <StepReview />}
            {store.step === 'thank_you' && <StepThankYou />}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
}
